/**
 * ReparatieIntentCard — vlag op de voorinspectie-detail-pagina.
 *
 * Toont het pre-pickup klantverzoek (vastgelegd door office of phone-agent)
 * en laat de inspecteur met één tap bevestigen of weerleggen. Status-changes
 * gaan via KledaFieldAppRest /v1/field/reparatie-intent/{confirm|deny} →
 * ReparatieIntentService met bron='Field-App'.
 *
 * Component verbergt zichzelf wanneer er geen klantverzoek is, of de status
 * al uit de Open-fase is (Bevestigd / Niet_Nodig).
 */

import { useState } from 'react';
import { WbButton, WbFlag, WbTextarea } from './wb';
import { salesforceClient } from '../lib/salesforce';
import type { VoorinspectieItem } from '../lib/salesforce';

interface Props {
  item: VoorinspectieItem;
  onChanged: () => void;
}

type Mode = 'idle' | 'confirming' | 'denying';

export function ReparatieIntentCard({ item, onChanged }: Props) {
  const [mode, setMode] = useState<Mode>('idle');
  const [notitie, setNotitie] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const klantverzoek = item.klantverzoekReparatie ?? null;
  const status = item.klantverzoekStatus ?? null;

  // Verberg wanneer geen klantverzoek of al beslist.
  if (!klantverzoek || klantverzoek.trim().length === 0) return null;
  if (status && status !== 'Open') return null;

  const handleSubmit = async (action: 'confirm' | 'deny') => {
    setSubmitting(true);
    setError(null);
    try {
      const trimmed = notitie.trim();
      const res = action === 'confirm'
        ? await salesforceClient.confirmReparatieIntent(item.itemId, trimmed || undefined)
        : await salesforceClient.denyReparatieIntent(item.itemId, trimmed || undefined);
      if (!res.success) {
        setError(res.message || `${action === 'confirm' ? 'Bevestigen' : 'Weerleggen'} mislukt`);
        setSubmitting(false);
        return;
      }
      setMode('idle');
      setNotitie('');
      setSubmitting(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
      setSubmitting(false);
    }
  };

  const denyDisabled = submitting || notitie.trim().length === 0;

  return (
    <div className="space-y-2">
      <WbFlag kind="opmerking" title="Klantverzoek: reparatie · open">
        <span className="block">"{klantverzoek}"</span>
        <span className="block text-sm">Bekijk dit kleed extra goed. Bevestig of meld dat het niet nodig is.</span>
      </WbFlag>

      {mode === 'idle' && (
        <div className="grid grid-cols-2 gap-2">
          <WbButton onClick={() => setMode('confirming')} disabled={submitting}>Bevestigen</WbButton>
          <WbButton variant="secondary" onClick={() => setMode('denying')} disabled={submitting}>Niet nodig</WbButton>
        </div>
      )}

      {(mode === 'confirming' || mode === 'denying') && (
        <div className="space-y-2">
          <WbTextarea
            label={mode === 'confirming' ? 'Notitie (optioneel)' : 'Waarom is reparatie niet nodig? (verplicht)'}
            value={notitie}
            onChange={(e) => setNotitie(e.target.value.slice(0, 1024))}
            rows={2}
            maxLength={1024}
            placeholder={mode === 'confirming' ? 'Bijv. kwastjes inderdaad los, repareren kan' : 'Bijv. kwastjes zijn nog goed, geen reparatie nodig'}
            disabled={submitting}
            autoFocus
            fout={error}
          />
          <div className="grid grid-cols-2 gap-2">
            <WbButton onClick={() => handleSubmit(mode === 'confirming' ? 'confirm' : 'deny')} disabled={mode === 'denying' ? denyDisabled : submitting} isLoading={submitting}>
              Versturen
            </WbButton>
            <WbButton variant="secondary" onClick={() => { setMode('idle'); setNotitie(''); setError(null); }} disabled={submitting}>
              Terug
            </WbButton>
          </div>
        </div>
      )}
    </div>
  );
}
